/* cart-giftnote.js
   Slice 1: occasion bridge for the cart page. Reads the occasion resolved by
   snippets/cart-giftnote.liquid and writes it into the Giftship Cart Option
   Set hidden input named _occasion, then asks Giftship to re-run its display
   rules. Same pattern as assets/giftship-occasion-bridge.js on product pages.
   Writes on every run because Giftship persists the hidden input value in the
   cart until something rewrites it (verified 2026-10-02). Hidden Input is
   Giftship's sanctioned theme-JS write path.
   Slice 2: card look (restyled 2026-10-29). Finds the card the customer
   picked in the visible Giftship gallery (its swatch background image), how
   much text sits in the gift message fields, the message's character count
   and line count, and whether the helper banner sits right above the card,
   and writes all of it onto the THEME's own .cr-cta wrapper as CSS variables
   (--gn-card, --gn-count, --gn-msg-h) and data attributes (data-gn,
   data-gn-card, data-gn-size, data-gn-full, data-gn-banner, data-gn-lang).
   "Choose for me" is never painted onto the card. assets/cart-giftnote.css
   draws Giftship's gift message block as the card, the copy, the tiles, the
   banner and the counter from those.
   Slice 3b: the AI helper. A Giftship HTML block in the same Cart Option Set
   holds <button data-gn-help>. This script listens for its click and opens a
   theme-owned dialog appended to <body> (v2, 2026-10-28: prefilled dropdowns,
   a details box, a results view), get 3 notes from the glitter-giftnote
   worker, or have the worker polish the words already on the card. "Use this"
   writes the chosen note into #gsMessage with input and change events, the
   write Gist approved on ticket 242497 (2026-09-28). A GET /health at start
   hides the button (data-gn-nohelp) when the worker is at its daily cap.
   Read-only against Giftship: nothing is written into its DOM apart from the
   value of the message field, no observers, no elements inserted into it. */
(function () {
  'use strict';
  var INPUT = '[data-child-name="_occasion"] input';
  var MARK = '[data-cr-giftnote]';
  var CONTAINER = '#gsAppContainer';
  var GALLERY = '[data-child-type="gallery"]';
  var MESSAGE = '[data-child-name="Gift-Message"]';
  var FIELDS = ['to_message', 'gsMessage', 'from_message'];
  var WORKER = 'https://glitter-giftnote.glitter-baskets.workers.dev';
  /* The browser's own fetch, captured at script start, before any later
     script can wrap it (Tommy's ruling 2026-11-03, after that day's timing
     probe). Giftship's theme app extension script,
     giftship-shipping-address.js, replaces window.fetch with a Proxy whose
     apply trap holds every POST to /cart/update.js for 2000 ms, store-wide,
     while the browser's own fetch answers in about 150 ms. That hold is why
     every Yes sat on "One moment..." for the full 2 s cap. This reference
     is used ONLY for our own destination-answer save (destSave below).
     Nothing else changes: window.fetch is never written, Giftship's proxy
     is never modified or removed, no other request is touched, and the
     keepalive flag, the awaited response, the 2 s cap, the held button and
     "One moment..." all stay. If this script ever runs AFTER the extension,
     the reference captured here is the wrapped fetch and behaviour is
     exactly what it was before this change, so nothing may depend on the
     capture being pristine. */
  var SAVE_FETCH = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;

  /* ---- slice 1: occasion bridge (unchanged) ---- */
  function resolve() {
    var el = document.querySelector(MARK);
    var occ = el ? el.getAttribute('data-occasion') : '';
    return occ || 'brand';
  }

  function bridge() {
    var input = document.querySelector(INPUT);
    if (!input) return;
    var occ = resolve();
    if (input.value !== occ) {
      input.value = occ;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    try {
      if (window.Gs && Gs.cartOptions && Gs.cartOptions.actions &&
          typeof Gs.cartOptions.actions.checkConditions === 'function') {
        Gs.cartOptions.actions.checkConditions();
      }
    } catch (e) { /* never block the cart */ }
  }

  /* ---- slice 2: card look (restyled 2026-10-29) ----
     Everything here is written on the THEME's own .cr-cta wrapper, never
     into Giftship's DOM: the card image (--gn-card), the text size step, the
     language, the character count (--gn-count, drawn by a CSS counter under
     the card) and whether the helper banner block sits directly above the
     card (data-gn-banner), which decides where the step 2 copy paints. The
     card is a fixed shape (round 3, 2026-10-29): the text size step is what
     makes the words fit, the card never grows.
     "Choose for me" is the one gallery tile that is never painted onto the
     card: the neutral cream card stays. */
  var CHOOSE = 'Choose for me';
  var LIMIT = 500;

  function host() {
    var c = document.querySelector(CONTAINER);
    return c && c.closest ? c.closest('.cr-cta') : null;
  }

  function chosenCard() {
    var c = document.querySelector(CONTAINER);
    if (!c) return null;
    var radios = c.querySelectorAll(GALLERY + ' input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) {
      var r = radios[i];
      var block = r.closest(GALLERY);
      if (!block || block.offsetParent === null) continue; /* gallery hidden by its block condition */
      var label = r.closest('label') || r.parentElement;
      var swatch = label ? label.querySelector('.gs__custom-checkbox') : null;
      if (!swatch) continue;
      var bg = swatch.style.backgroundImage || getComputedStyle(swatch).backgroundImage || '';
      var m = bg.match(/url\(["']?([^"')]+)["']?\)/);
      if (m) return { url: m[1], name: r.value || '' };
    }
    return null;
  }

  function textLength() {
    var n = 0;
    for (var i = 0; i < FIELDS.length; i++) {
      var el = document.getElementById(FIELDS[i]);
      if (el && el.value) n += el.value.length;
    }
    return n;
  }

  function sizeStep(n) {
    return n <= 70 ? 1 : n <= 160 ? 2 : n <= 280 ? 3 : n <= 400 ? 4 : 5;
  }


  /* the message's own content height, for the centring (Tommy's ruling
     2026-11-02: a short message sits centred between To and From at the
     large step and grows from the centre until it fills the space). It is
     measured on a THEME-owned mirror of the textarea's text, same width,
     same font, same line height, appended to <body> like the dialog, never
     on the textarea itself: a textarea's scrollHeight can never report less
     than the box's current height, so a message that got shorter would never
     shrink back to the centre. The mirror takes the textarea's border-box
     width, which is the width the text wraps at once the box fits it and no
     scrollbar is drawn. An empty message measures its placeholder, so the
     placeholder sits centred too. Nothing is written on the textarea. */
  var mirror = null;
  function contentHeight(ta) {
    if (!ta) return 0;
    var w = ta.getBoundingClientRect().width;
    if (!w) return 0;
    if (!mirror || !mirror.parentNode) {
      mirror = document.createElement('div');
      mirror.className = 'gn-measure';
      mirror.setAttribute('aria-hidden', 'true');
      (document.body || document.documentElement).appendChild(mirror);
    }
    var cs = getComputedStyle(ta);
    mirror.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;' +
      'white-space:pre-wrap;overflow-wrap:break-word;word-wrap:break-word;box-sizing:border-box;' +
      'margin:0;border:0;padding:0;height:auto;width:' + w + 'px;' +
      'font-family:' + cs.fontFamily + ';font-size:' + cs.fontSize + ';font-weight:' + cs.fontWeight +
      ';font-style:' + cs.fontStyle + ';line-height:' + cs.lineHeight + ';letter-spacing:' + cs.letterSpacing +
      ';text-align:' + cs.textAlign + ';';
    var text = ta.value || ta.placeholder || '';
    mirror.textContent = (!text || /\n$/.test(text)) ? text + '\u200b' : text;
    return Math.ceil(mirror.getBoundingClientRect().height);
  }

  /* the helper banner is the Giftship HTML block holding [data-gn-help];
     "above" when it is the visible block right before the card */
  function bannerAbove() {
    var msg = document.querySelector(CONTAINER + ' ' + MESSAGE);
    var prev = msg ? msg.previousElementSibling : null;
    return !!(prev && prev.querySelector && prev.querySelector('[data-gn-help]') && prev.offsetParent !== null);
  }

  function paint() {
    var h = host();
    if (!h) return;
    if (!document.querySelector(CONTAINER + ' ' + MESSAGE)) return;
    h.setAttribute('data-gn', '');
    h.setAttribute('data-gn-lang', uiLang());
    var card = chosenCard();
    if (card && card.name !== CHOOSE) {
      h.style.setProperty('--gn-card', 'url("' + card.url.replace(/["\\]/g, '') + '")');
    } else {
      h.style.removeProperty('--gn-card');
    }
    if (card) h.setAttribute('data-gn-card', card.name);
    else h.removeAttribute('data-gn-card');
    h.setAttribute('data-gn-size', String(sizeStep(textLength())));
    var ta = document.getElementById('gsMessage');
    /* measured after the size step is on the wrapper, so the mirror wraps
       the words at the type they will be drawn in */
    h.style.setProperty('--gn-msg-h', contentHeight(ta) + 'px');
    var n = ta && ta.value ? ta.value.length : 0;
    h.style.setProperty('--gn-count', String(n));
    if (n >= LIMIT) h.setAttribute('data-gn-full', '');
    else h.removeAttribute('data-gn-full');
    if (bannerAbove()) h.setAttribute('data-gn-banner', 'above');
    else h.removeAttribute('data-gn-banner');
    healthOnce();
  }

  /* ---- slice 3b: the AI helper, dialog v2 (2026-10-28) ----
     Layout per Tommy's approved mock: cream panel, serif title, two
     underlined tabs, native dropdowns instead of chips, everything prefilled
     from the basket and the storefront so the first click can already be
     "Suggest 3 messages", a free-text "Anything you'd like to include?" box
     (worker v3 field details, 200 characters), and a results view inside the
     same dialog with "Use this message", "Try again" and "Back to
     preferences" (the choices are kept). The polish tab shows the card's
     words read-only (the card is the editor) and offers the worker's four
     fixes. Same write path as before: "Use this message" writes #gsMessage
     with input and change events (Gist ticket 242497). Nothing is inserted
     into Giftship's DOM; the dialog is appended to <body>. */
  var STR = {
    en: {
      title: 'A little help finding the words', close: 'Close',
      sub: 'Create a thoughtful message or give yours a finishing touch.',
      forWho: 'Card for',
      tabWrite: 'Write a message', tabPolish: 'Polish my message',
      occ: "What's the occasion?", occPh: 'Choose an occasion', fromBasket: 'from your basket',
      rel: 'Your relationship', relPh: 'Choose a relationship', optional: '(optional)',
      style: 'Message style', details: "Anything you'd like to include?",
      detailsPh: "A name, a shared memory, or something you'd like to say...",
      lang: 'Language', other: 'Something else', otherPh: 'Tell us the occasion',
      go: 'Suggest 3 messages', polishGo: 'Polish my message', writing: 'Working on it...',
      draftLabel: 'Your message, as written on the card', fix: 'Message style',
      use: 'Use this message', again: 'Try again', back: 'Back to preferences',
      resultsTitle: 'Here are three ideas', resultsTitlePolish: 'Here are two versions',
      foot: 'You can edit your message before adding it to the card.',
      empty: 'Write a few words on the card first, then come back here.',
      busy: 'You have used the helper a lot this hour. Your own words are perfect, and the card is ready for them.',
      down: 'The helper could not answer just now. Your choices are kept; please try again in a moment.',
      occNames: { christmas: 'Christmas', mothers: "Mother's Day", fathers: "Father's Day", valentines: "Valentine's Day", sympathy: 'Sympathy', babyboy: 'Baby boy', babygirl: 'Baby girl', babyneutral: 'New baby', getwell: 'Get well', birthday: 'Birthday', easter: 'Easter', thanks: 'Thanksgiving', diwali: 'Diwali', hanukkah: 'Hanukkah', corporate: 'Corporate thank you' },
      occs: [['birthday', 'Birthday'], ['thankyou', 'Thank you'], ['corporate', 'Corporate thank you'], ['congratulations', 'Congratulations'], ['getwell', 'Get well'], ['sympathy', 'Sympathy'], ['justbecause', 'Just because']],
      rels: [['friend', 'Friend'], ['family', 'Family'], ['colleague', 'Colleague'], ['client', 'Client'], ['partner', 'Partner']],
      tones: [['warm', 'Warm & thoughtful'], ['heartfelt', 'Heartfelt'], ['funny', 'Light & playful'], ['formal', 'Formal']],
      fixes: [['warmer', 'Warmer'], ['shorter', 'Shorter'], ['formal', 'More formal'], ['spelling', 'Fix spelling']]
    },
    fr: {
      title: 'Un peu d’aide pour trouver les mots', close: 'Fermer',
      sub: 'Composez un message attentionné ou donnez au vôtre une touche finale.',
      forWho: 'Carte pour',
      tabWrite: 'Écrire un message', tabPolish: 'Peaufiner mon message',
      occ: "Quelle est l'occasion?", occPh: 'Choisir une occasion', fromBasket: 'selon votre panier',
      rel: 'Votre lien', relPh: 'Choisir un lien', optional: '(facultatif)',
      style: 'Style du message', details: 'Quelque chose à inclure?',
      detailsPh: 'Un prénom, un souvenir partagé ou ce que vous aimeriez dire...',
      lang: 'Langue', other: 'Autre', otherPh: "Dites-nous l'occasion",
      go: 'Proposer 3 messages', polishGo: 'Peaufiner mon message', writing: 'Un instant...',
      draftLabel: 'Votre message, tel qu’écrit sur la carte', fix: 'Style du message',
      use: 'Utiliser ce message', again: 'Réessayer', back: 'Retour aux préférences',
      resultsTitle: 'Voici trois idées', resultsTitlePolish: 'Voici deux versions',
      foot: 'Vous pourrez modifier votre message avant de l’ajouter à la carte.',
      empty: "Écrivez d'abord quelques mots sur la carte, puis revenez ici.",
      busy: "Vous avez beaucoup utilisé l'aide cette heure-ci. Vos propres mots sont parfaits, et la carte est prête à les recevoir.",
      down: "L'aide n'a pas pu répondre pour l'instant. Vos choix sont conservés; réessayez dans un moment.",
      occNames: { christmas: 'Noël', mothers: 'Fête des Mères', fathers: 'Fête des Pères', valentines: 'Saint-Valentin', sympathy: 'Sympathie', babyboy: 'Bébé garçon', babygirl: 'Bébé fille', babyneutral: 'Nouveau bébé', getwell: 'Prompt rétablissement', birthday: 'Anniversaire', easter: 'Pâques', thanks: 'Action de grâce', diwali: 'Diwali', hanukkah: 'Hanoukka', corporate: 'Remerciement d’affaires' },
      occs: [['birthday', 'Anniversaire'], ['thankyou', 'Merci'], ['corporate', 'Remerciement d’affaires'], ['congratulations', 'Félicitations'], ['getwell', 'Prompt rétablissement'], ['sympathy', 'Sympathie'], ['justbecause', 'Sans raison']],
      rels: [['friend', 'Ami(e)'], ['family', 'Famille'], ['colleague', 'Collègue'], ['client', 'Client(e)'], ['partner', 'Partenaire']],
      tones: [['warm', 'Chaleureux et attentionné'], ['heartfelt', 'Touchant'], ['funny', 'Léger et amusant'], ['formal', 'Formel']],
      fixes: [['warmer', 'Plus chaleureux'], ['shorter', 'Plus court'], ['formal', 'Plus formel'], ['spelling', "Corriger l'orthographe"]]
    }
  };
  var LANGS = [['en', 'English'], ['fr', 'Français']];

  function uiLang() {
    var l = (window.Shopify && Shopify.locale) || document.documentElement.lang || 'en';
    return String(l).slice(0, 2).toLowerCase() === 'fr' ? 'fr' : 'en';
  }

  var healthDone = false;
  function healthOnce() {
    if (healthDone || typeof fetch !== 'function') return;
    healthDone = true;
    fetch(WORKER + '/health', { method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var h = host();
        if (h && j && j.ok === false) { h.setAttribute('data-gn-nohelp', ''); paint(); } /* the banner hides, so the step 2 copy moves to the card */
      })
      .catch(function () { /* leave the button; the click path handles errors */ });
  }

  function el(tag, cls, text) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  var dlg = null, ui = null, T = null, opener = null, uid = 0;
  var sel = { mode: 'write', occ: '', other: '', rel: '', tone: 'warm', lang: 'en', fix: 'warmer', details: '' };

  /* a labelled field: <div><label>text (optional)</label>control</div> */
  function field(labelText, control, optional, inline) {
    var wrap = el('div', inline ? 'gn-dlg__field gn-dlg__field--inline' : 'gn-dlg__field');
    var lab = el('label', 'gn-dlg__label', optional ? labelText + ' ' : labelText);
    if (optional) lab.appendChild(el('small', null, T.optional));
    if (control.id) lab.htmlFor = control.id;
    wrap.appendChild(lab);
    wrap.appendChild(control);
    return wrap;
  }

  /* a native select bound to sel[group]; placeholder is a disabled-looking first option */
  function select(items, group, placeholder) {
    var s = el('select', 'gn-dlg__select');
    s.id = 'gn-dlg-' + group + '-' + (++uid);
    if (placeholder) { var ph = el('option', null, placeholder); ph.value = ''; s.appendChild(ph); }
    items.forEach(function (it) { var o = el('option', null, it[1]); o.value = it[0]; s.appendChild(o); });
    s.addEventListener('change', function () {
      sel[group] = s.value;
      s.classList.toggle('is-empty', !s.value);
      if (group === 'occ') ui.other.hidden = sel.occ !== 'other';
      if (group === 'lang') { ui.langW.value = sel.lang; ui.langP.value = sel.lang; }
    });
    return s;
  }

  /* the next holidays from the theme's calendar, written by
     snippets/cart-giftnote.liquid on the marker as data-upcoming="christmas,valentines" */
  function upcoming() {
    var m = document.querySelector(MARK);
    var s = m ? (m.getAttribute('data-upcoming') || '') : '';
    return s ? s.split(',') : [];
  }

  /* the occasion list: the basket's occasion first (preselected, marked
     "from your basket"), then the next two holidays from the theme calendar,
     then the plain-cart choices, then Something else */
  function fillOccasions() {
    var s = ui.occ;
    while (s.firstChild) s.removeChild(s.firstChild);
    var known = resolve();
    if (known === 'brand') known = '';
    if (known) {
      var k = el('option', null, (T.occNames[known] || known) + ' (' + T.fromBasket + ')');
      k.value = known; s.appendChild(k);
    } else {
      var ph = el('option', null, T.occPh); ph.value = ''; s.appendChild(ph);
    }
    var added = {}; added[known] = true;
    upcoming().forEach(function (k) {
      if (added[k]) return;
      var u = el('option', null, T.occNames[k] || k); u.value = k; s.appendChild(u); added[k] = true;
    });
    T.occs.forEach(function (it) {
      if (added[it[0]]) return;
      var o = el('option', null, it[1]); o.value = it[0]; s.appendChild(o); added[it[0]] = true;
    });
    var ot = el('option', null, T.other); ot.value = 'other'; s.appendChild(ot);
    sel.occ = known; sel.other = '';
    s.value = known;
    ui.other.value = ''; ui.other.hidden = true;
  }

  function paintControls() {
    if (!dlg) return;
    ui.tabWrite.setAttribute('aria-selected', sel.mode === 'write' ? 'true' : 'false');
    ui.tabPolish.setAttribute('aria-selected', sel.mode === 'polish' ? 'true' : 'false');
    ui.panelWrite.hidden = sel.mode !== 'write';
    ui.panelPolish.hidden = sel.mode !== 'polish';
    ui.occ.value = sel.occ; ui.rel.value = sel.rel; ui.tone.value = sel.tone; ui.fix.value = sel.fix;
    ui.langW.value = sel.lang; ui.langP.value = sel.lang;
    ui.occ.classList.toggle('is-empty', !sel.occ);
    ui.rel.classList.toggle('is-empty', !sel.rel);
    ui.other.hidden = sel.occ !== 'other';
  }

  function build() {
    T = STR[uiLang()];
    sel.lang = uiLang();
    dlg = el('dialog', 'gn-dlg');
    dlg.setAttribute('aria-labelledby', 'gn-dlg-title');
    var box = el('div', 'gn-dlg__box');
    dlg.appendChild(box);

    var head = el('div', 'gn-dlg__head');
    var title = el('p', 'gn-dlg__title', T.title); title.id = 'gn-dlg-title';
    title.setAttribute('role', 'heading'); title.setAttribute('aria-level', '2');
    var close = el('button', 'gn-dlg__close', '×'); close.type = 'button'; close.setAttribute('aria-label', T.close);
    close.addEventListener('click', function () { dlg.close(); });
    head.appendChild(title); head.appendChild(close); box.appendChild(head);
    box.appendChild(el('p', 'gn-dlg__sub', T.sub));
    var forLine = el('p', 'gn-dlg__for'); forLine.hidden = true; box.appendChild(forLine);

    /* the form: tabs, two panels, inline message, footnote */
    var form = el('div', 'gn-dlg__form');
    var tabs = el('div', 'gn-dlg__tabs'); tabs.setAttribute('role', 'tablist');
    var tabWrite = el('button', 'gn-dlg__tab', T.tabWrite); tabWrite.type = 'button'; tabWrite.setAttribute('role', 'tab');
    var tabPolish = el('button', 'gn-dlg__tab', T.tabPolish); tabPolish.type = 'button'; tabPolish.setAttribute('role', 'tab');
    tabWrite.addEventListener('click', function () { sel.mode = 'write'; hideMsg(); paintControls(); });
    tabPolish.addEventListener('click', function () { sel.mode = 'polish'; hideMsg(); paintControls(); });
    tabs.appendChild(tabWrite); tabs.appendChild(tabPolish); form.appendChild(tabs);

    /* write panel */
    var pw = el('div', 'gn-dlg__panel');
    var occ = select([], 'occ');
    var other = el('input', 'gn-dlg__input'); other.type = 'text'; other.maxLength = 40; other.placeholder = T.otherPh; other.hidden = true;
    other.addEventListener('input', function () { sel.other = other.value; });
    var occField = field(T.occ, occ); occField.appendChild(other); pw.appendChild(occField);
    var row = el('div', 'gn-dlg__row');
    var rel = select(T.rels, 'rel', T.relPh);
    var tone = select(T.tones, 'tone');
    row.appendChild(field(T.rel, rel, true)); row.appendChild(field(T.style, tone)); pw.appendChild(row);
    var details = el('textarea', 'gn-dlg__textarea'); details.id = 'gn-dlg-details'; details.maxLength = 200; details.rows = 3; details.placeholder = T.detailsPh;
    details.addEventListener('input', function () { sel.details = details.value; });
    pw.appendChild(field(T.details, details, true));
    var langW = select(LANGS, 'lang');
    pw.appendChild(field(T.lang, langW, false, true));
    var go = el('button', 'gn-dlg__go', T.go); go.type = 'button';
    go.addEventListener('click', function () { send('write'); });
    pw.appendChild(go); form.appendChild(pw);

    /* polish panel: the card's words, read-only, plus the fix and the language */
    var pp = el('div', 'gn-dlg__panel'); pp.hidden = true;
    var draft = el('div', 'gn-dlg__draft'); draft.id = 'gn-dlg-draft';
    pp.appendChild(field(T.draftLabel, draft));
    var rowP = el('div', 'gn-dlg__row');
    var fix = select(T.fixes, 'fix');
    var langP = select(LANGS, 'lang');
    rowP.appendChild(field(T.fix, fix)); rowP.appendChild(field(T.lang, langP)); pp.appendChild(rowP);
    var goP = el('button', 'gn-dlg__go', T.polishGo); goP.type = 'button';
    goP.addEventListener('click', function () { send('polish'); });
    pp.appendChild(goP); form.appendChild(pp);

    var msg = el('p', 'gn-dlg__msg'); msg.hidden = true; msg.setAttribute('role', 'alert'); form.appendChild(msg);
    form.appendChild(el('p', 'gn-dlg__foot', T.foot));
    box.appendChild(form);

    /* the results view replaces the form inside the same dialog */
    var results = el('div', 'gn-dlg__results'); results.hidden = true; box.appendChild(results);

    /* click on the backdrop closes; focus goes back to the pill on close */
    dlg.addEventListener('click', function (e) { if (e.target === dlg) dlg.close(); });
    dlg.addEventListener('close', function () { if (opener && typeof opener.focus === 'function') { try { opener.focus(); } catch (e) { /* ignore */ } } });

    ui = { forLine: forLine, form: form, tabWrite: tabWrite, tabPolish: tabPolish, panelWrite: pw, panelPolish: pp,
           occ: occ, other: other, rel: rel, tone: tone, details: details, langW: langW, fix: fix, langP: langP,
           go: go, goP: goP, draft: draft, msg: msg, results: results, box: box };
    document.body.appendChild(dlg);
  }

  function clearResults() {
    while (ui.results.firstChild) ui.results.removeChild(ui.results.firstChild);
  }

  function showForm() {
    ui.results.hidden = true;
    clearResults();
    ui.form.hidden = false;
    try { ui.box.scrollTop = 0; } catch (e) { /* ignore */ }
  }

  function showMsg(text) { ui.msg.textContent = text; ui.msg.hidden = false; }
  function hideMsg() { ui.msg.textContent = ''; ui.msg.hidden = true; }

  function openDialog(btn) {
    if (!dlg) build();
    opener = btn || document.activeElement || null;
    var to = document.getElementById('to_message');
    var msg = document.getElementById('gsMessage');
    var name = to ? to.value.trim() : '';
    ui.forLine.textContent = name ? T.forWho + ' ' + name : '';
    ui.forLine.hidden = !name;
    ui.draft.textContent = (msg && msg.value.trim()) || T.empty;
    fillOccasions();
    showForm();
    hideMsg();
    paintControls();
    if (typeof dlg.showModal === 'function') { if (!dlg.open) dlg.showModal(); }
    else { dlg.setAttribute('open', ''); dlg.classList.add('gn-dlg--fallback'); }
  }

  function showNotes(notes, mode) {
    clearResults();
    var back = el('button', 'gn-dlg__back', '‹ ' + T.back); back.type = 'button';
    back.addEventListener('click', showForm);
    ui.results.appendChild(back);
    ui.results.appendChild(el('p', 'gn-dlg__rtitle', mode === 'polish' ? T.resultsTitlePolish : T.resultsTitle));
    notes.forEach(function (n) {
      var card = el('div', 'gn-dlg__note');
      card.appendChild(el('p', null, n));
      var use = el('button', 'gn-dlg__use', T.use); use.type = 'button';
      use.addEventListener('click', function () { useNote(n); });
      card.appendChild(use);
      ui.results.appendChild(card);
    });
    var again = el('button', 'gn-dlg__again', T.again); again.type = 'button';
    again.addEventListener('click', function () { send(mode, again); });
    ui.results.appendChild(again);
    ui.form.hidden = true;
    ui.results.hidden = false;
    try { ui.box.scrollTop = 0; back.focus(); } catch (e) { /* ignore */ }
  }

  function useNote(text) {
    var ta = document.getElementById('gsMessage');
    if (!ta) return;
    ta.value = text;
    ta.dispatchEvent(new Event('input', { bubbles: true }));
    ta.dispatchEvent(new Event('change', { bubbles: true }));
    paint();
    dlg.close();
    try { ta.focus(); } catch (e) { /* ignore */ }
  }

  /* a failed call goes back to the form with the choices intact and a note */
  function fail(text) { showForm(); showMsg(text); }

  var inflight = false;
  function send(mode, btnOverride) {
    if (inflight || typeof fetch !== 'function') return;
    var btn = btnOverride || (mode === 'polish' ? ui.goP : ui.go);
    var label = btn.textContent;
    var payload = { mode: mode, lang: sel.lang, occasion: resolve() };
    if (mode === 'write') {
      if (sel.occ === 'other') { payload.occasion = 'brand'; payload.occasion_text = (sel.other || '').slice(0, 40); }
      else if (sel.occ) { payload.occasion = sel.occ; }
      if (sel.rel) payload.relationship = sel.rel;
      payload.tone = sel.tone;
      var to = document.getElementById('to_message');
      payload.recipient = to ? to.value.slice(0, 40) : '';
      var d = (sel.details || '').trim();
      if (d) payload.details = d.slice(0, 200);
    } else {
      var msg = document.getElementById('gsMessage');
      var draft = msg ? msg.value.trim() : '';
      if (!draft) { showMsg(T.empty); return; }
      payload.draft = draft.slice(0, 500);
      payload.fix = sel.fix;
    }
    hideMsg();
    inflight = true; btn.disabled = true; btn.textContent = T.writing; btn.setAttribute('aria-busy', 'true');
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, 12000);
    fetch(WORKER + '/suggest', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      signal: ctrl ? ctrl.signal : undefined
    })
      .then(function (r) { return r.json().then(function (j) { return { status: r.status, body: j }; }); })
      .then(function (res) {
        if (res.status === 200 && res.body && Array.isArray(res.body.notes) && res.body.notes.length) showNotes(res.body.notes, mode);
        else if (res.status === 429) fail(T.busy);
        else fail(T.down);
      })
      .catch(function () { fail(T.down); })
      .then(function () { clearTimeout(timer); inflight = false; btn.disabled = false; btn.textContent = label; btn.removeAttribute('aria-busy'); });
  }

  /* ---- the one-question flow (2026-10-30) ----
     Where are the baskets going? The section renders the state on the
     .cr-cart root and on .cr-cta as data-gn-dest (none, ask, single, multi;
     see the comment at the top of sections/cart-redesign.liquid), computed
     in Liquid from the GB count and the saved cart attribute
     _gn_destinations. This block only moves between the states: it writes
     the attribute on the two THEME elements (the fold, the three states of
     the question element at the top of the page and the folded bar are all
     CSS on those), saves
     the answer with POST /cart/update.js so a reload paints the same state
     (a theme attribute survives Giftship's own blur-save, proven 2026-10-30),
     and repaints. Nothing here reads or writes Giftship's DOM. */
  var DEST_ROOT = '.cr-cart';
  function destRoot() { return document.querySelector(DEST_ROOT); }
  function destGet() {
    var r = destRoot();
    return r ? (r.getAttribute('data-gn-dest') || 'none') : 'none';
  }
  function destApply(v) {
    var r = destRoot();
    if (!r) return;
    r.setAttribute('data-gn-dest', v);
    var cta = r.querySelector('.cr-cta');
    if (cta) cta.setAttribute('data-gn-dest', v);
  }
  function destSave(v) {
    var f = SAVE_FETCH || (typeof window.fetch === 'function' ? window.fetch.bind(window) : null); /* the capture above, see SAVE_FETCH */
    if (!f) return null;
    return f('/cart/update.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ attributes: { _gn_destinations: v === 'ask' ? '' : v } }),
      keepalive: true /* survives the navigation when the answer is followed straight by Ship to multiple addresses or Secure checkout */
    }).catch(function () { /* the page state already shows the answer; the next reload re-asks at worst */ });
  }
  /* then: optional, runs once the save has answered or 2 s have passed,
     whichever comes first. The multi answer navigates away in it; waiting
     for the response is what makes the answer stick (keepalive alone lost
     the write once on the staging phone run, 2026-10-30). */
  function destSet(v, then) {
    if (destGet() === 'none') return; /* under the threshold there is nothing to answer */
    destApply(v);
    var saved = destSave(v);
    paint();
    if (!then) return;
    var done = false;
    var go = function () { if (!done) { done = true; then(); } };
    if (saved && typeof saved.then === 'function') saved.then(go, go); else go();
    setTimeout(go, 2000);
  }

  /* Giftship's own way back from the multiship cart (diagnostic and Tommy's
     ruling, both 2026-11-03). Its "Single address" header button writes its
     own cookie, current_ms_state=normal_cart, and navigates to /cart/
     without touching our attribute, so a shopper who had answered Yes and
     then pressed that button came back to the deep teal multi banner with
     the card folded. Liquid cannot read cookies, so this runs at script
     start, before Giftship is up, and again on a back-forward restore:
     normal_cart while the saved answer is multi means the shopper left by
     that button, and the answer becomes single (ruled SINGLE, not ask: the
     button says Single address). ms_cart, no cookie or any other saved
     answer: nothing happens, and under the threshold destSet is a no-op.
     Forward direction only: a split made on Giftship's own page and brought
     back to /cart is reverted by Giftship itself, which also blanks every
     cart attribute, so the question is simply asked again. The cookie is
     read, never written; nothing in Giftship's DOM is touched. */
  function msCookie() {
    var m = document.cookie.match(/(?:^|;\s*)current_ms_state=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : '';
  }
  function destReconcile() {
    if (msCookie() === 'normal_cart' && destGet() === 'multi') destSet('single');
  }
  destReconcile();

  /* Into Giftship's multiship cart. When Giftship is up, its own entry point,
     Gs.cartPage.actions.showMulti(), the call its deprecated alias names as
     the supported one: it adds the locale root, writes Giftship's
     current_ms_state cookie and navigates. Otherwise the theme anchor's
     /a/gs/cart/ href. No Giftship element is clicked. Used by Continue on
     the multi state of the question element (the banner is the question,
     2026-11-02), by the generic banner's CTA under the threshold and, ruled
     2026-10-31, by the Yes answer itself: that answer saves multi
     (keepalive, so the save outlives the navigation) and goes straight
     here; the deep teal multi state and the folded bar are for a shopper
     who comes BACK to the cart with multi saved. */
  function goMulti(href) {
    try {
      if (window.Gs && Gs.cartPage && Gs.cartPage.actions && typeof Gs.cartPage.actions.showMulti === 'function') {
        Gs.cartPage.actions.showMulti();
        return;
      }
    } catch (err) { /* fall through to the href */ }
    window.location.href = href || '/a/gs/cart/';
  }
  function multiHref() {
    var r = destRoot();
    var a = r ? r.querySelector('[data-gn-multi-go], .cr-multiship__btn, .cr-multiship-mini') : null;
    return a && a.href ? a.href : '/a/gs/cart/';
  }
  function routeMulti(e, a) {
    e.preventDefault();
    goMulti(a && a.href ? a.href : multiHref());
  }

  /* ---- wiring ---- */
  function run() {
    bridge();
    destApply(destGet()); /* keep .cr-cta in step with the root after a Giftship repaint */
    paint();
    setTimeout(paint, 400); /* again after Giftship has swapped the gallery */
  }

  document.addEventListener('giftship.loaded', run);
  document.addEventListener('giftship.reloaded', run);
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t && FIELDS.indexOf(t.id) !== -1) paint();
  });
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(CONTAINER + ' ' + GALLERY)) paint();
  });
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-gn-help]') : null;
    if (!b) return;
    e.preventDefault();
    openDialog();
  });
  /* "Yes, different addresses" while the save-then-navigate wait runs: the
     label reads "One moment..." (FR "Un instant..."), the moment it is
     clicked (Tommy, 2026-11-02). Only the button's text node changes (an
     icon, if one is ever added, stays); the original label is kept on the
     button so a page the browser restores from its back-forward cache (Back
     from the multiship cart) gets its button back, see the pageshow
     listener below. Theme button, never Giftship's. */
  var WAIT = { en: 'One moment...', fr: 'Un instant...' };
  function holdButton(b) {
    b.disabled = true; b.setAttribute('aria-busy', 'true'); /* held while the save answers */
    for (var i = b.childNodes.length - 1; i >= 0; i--) {
      var n = b.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) {
        if (!b.hasAttribute('data-gn-label')) b.setAttribute('data-gn-label', n.nodeValue);
        n.nodeValue = WAIT[uiLang()];
        return;
      }
    }
    if (!b.hasAttribute('data-gn-label')) b.setAttribute('data-gn-label', b.textContent);
    b.textContent = WAIT[uiLang()];
  }
  function releaseButton(b) {
    b.disabled = false; b.removeAttribute('aria-busy');
    var label = b.getAttribute('data-gn-label');
    if (label == null) return;
    for (var i = b.childNodes.length - 1; i >= 0; i--) {
      var n = b.childNodes[i];
      if (n.nodeType === 3 && n.nodeValue.trim()) { n.nodeValue = label; return; }
    }
    b.textContent = label;
  }
  /* Yes: save multi, wait for the save (2 s cap) and leave. The page is
     NOT repainted to the multi state first (it was on 2026-10-31): a
     repaint swaps the question for the teal banner, and with it hides the
     button that now reads "One moment...", so the question stays as it is
     until the multiship cart takes over. The teal banner and the folded
     bar are painted from the saved attribute when the shopper comes back,
     by a reload or by the pageshow listener below. */
  function destLeave(then) {
    var saved = destSave('multi');
    var done = false;
    var go = function () { if (!done) { done = true; then(); } };
    if (saved && typeof saved.then === 'function') saved.then(go, go); else go();
    setTimeout(go, 2000);
  }
  /* the answer buttons: Yes and No in the ask state, Change on the single
     line and on the multi banner, Open on the folded bar. Yes saves and
     leaves for the multiship cart at once (ruled 2026-10-31). */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-gn-dest-choose]') : null;
    if (!b) return;
    e.preventDefault();
    var v = b.getAttribute('data-gn-dest-choose') || 'ask';
    if (v === 'multi' && destGet() !== 'none') {
      holdButton(b);
      destLeave(function () { goMulti(multiHref()); });
      return;
    }
    destSet(v);
  });
  /* Back from the multiship cart can restore this page from the browser's
     back-forward cache exactly as it was left: the question with Yes still
     held. Release the button and paint the saved answer, the teal banner at
     the top and the folded bar, which is what a reload shows. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var held = document.querySelectorAll('[data-gn-dest-choose="multi"][aria-busy="true"]');
    for (var i = 0; i < held.length; i++) releaseButton(held[i]);
    if (held.length) { destApply('multi'); paint(); }
    destReconcile(); /* a restored page reads Giftship's cookie again, see above */
  });
  /* Secure checkout while the card is folded: unfold first, so Giftship's
     required-field validation lands on a visible card, then let the click
     go on. Capture phase, so it runs before Giftship's own checkout handler
     and before the sticky bar's proxy click reaches the real button. */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('button[name="checkout"], [data-cr-sticky-checkout]') : null;
    if (!b) return;
    var d = destGet();
    if (d === 'multi' || d === 'ask') destSet('single');
  }, true);
  /* Continue on the multi state, and under the threshold the generic
     banner's CTA, both the full banner and the one-line prompt */
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('[data-gn-multi-go], .cr-multiship__btn, .cr-multiship-mini') : null;
    if (a) routeMulti(e, a);
  });

  /* Wait, bounded, for the Giftship hidden input to appear, then run once:
     40 tries at 250 ms, 10 s cap, stops the moment the input exists.
     Read-only polling, no DOM mutation. The 2026-10-23 reading was that
     giftship.loaded does not fire on the cart page; the 2026-10-30 probe
     saw it fire at 0.9 to 1.3 s on every load, BEFORE the Cart Option Set
     blocks exist, so the listener above runs too early and this wait is
     what actually lands the first write. Both stay. */
  var tries = 0;
  (function wait() {
    if (document.querySelector(INPUT)) { run(); return; }
    if (++tries < 40) { setTimeout(wait, 250); }
  })();
})();
